import { getCookie } from 'cookies-next/client';

import { DEFAULT_TIMEOUT_MS, INTERNAL_ROUTE_HOSTS } from './constants';
import {
  isJSONResponse,
  isOctetStreamResponse,
  isTextResponse,
  queryParamsFromObject,
} from './helpers';
import {
  type GenericPresentationPath,
  type RequestBody,
  type SuccessResponse,
} from './type-helpers';
import {
  AxiosCompatibleError,
  type AxiosCompatibleResponse,
  type RequestConfig,
} from './types';

import {
  ACCESS_TOKEN_KEY,
  PREVIEW_ENV_KEY,
  USER_ID_KEY,
} from '@/constants/cookies';
import {
  H_AUTHORIZATION,
  H_DEPOP_DEVICE_ID,
  X_COUNTRY_CODE,
  X_ENV_NAME,
  X_REGION_CODE,
  X_USER_AGENT,
  X_USER_ID,
} from '@/constants/headers';
import { getPersistentId } from '@/modules/activityTracker/helpers';
import { isServerCheck } from '@/modules/env/isServerCheck';
import { Stdv1Stat } from '@/modules/observability/constants';
import {
  TURNSTILE_CHALLENGE_RESULT,
  TURNSTILE_EVENT_MODAL_TRIGGER,
} from '@/modules/turnstile/constants';
import { isChallengeResponse } from '@/modules/turnstile/helpers';
import { type TurnstileChallengeResult } from '@/modules/turnstile/types';

const nextHeaders = import('next/headers');

// Headers to exclude when forwarding incoming request headers for internal routes.
// cookie/x-real-client-ip: high-cardinality, breaks Next.js fetch caching (unique cache key per user)
// host: must match the internal service, not the original public host
// connection/keep-alive/transfer-encoding: hop-by-hop, not meant for upstream
// content-length: server requests mismatch actual content length
const INTERNAL_ROUTE_EXCLUDED_HEADERS = new Set([
  'cookie',
  'x-real-client-ip',
  'host',
  'connection',
  'keep-alive',
  'transfer-encoding',
  'content-length',
]);

function createTimeoutSignal(ms?: number): {
  signal: AbortSignal;
  clear: () => void;
} {
  const controller = new AbortController();
  const id = setTimeout(
    () =>
      controller.abort(
        new DOMException('The operation timed out.', 'TimeoutError')
      ),
    ms ?? DEFAULT_TIMEOUT_MS
  );
  return { signal: controller.signal, clear: () => clearTimeout(id) };
}

/**
 * Modify request headers to add optional auth and depop-specifics
 */
async function modifyRequestHeaders(config: RequestConfig) {
  const headersFunction = await nextHeaders;
  const h = new Headers(config.headers);

  //only set Content-Type if req body is present and Content-Type is not already set
  if (config.data && !h.get('Content-Type')) {
    h.set('Content-Type', 'application/json');
  }

  if (isServerCheck()) {
    h.set('Accept-Encoding', 'gzip');
    // Forward the user's x-country-code and x-region-code to ensure that the headers of the server is not applied during server side requests
    if (config.withGeoHeaders !== false) {
      const countryCode =
        config?.headers?.[X_COUNTRY_CODE] ||
        (await headersFunction.headers()).get(X_COUNTRY_CODE)?.toUpperCase();

      if (countryCode) {
        h.set(X_COUNTRY_CODE, countryCode);
      }

      const regionCode =
        config?.headers?.[X_REGION_CODE] ||
        (await headersFunction.headers()).get(X_REGION_CODE)?.toUpperCase();

      if (regionCode) {
        h.set(X_REGION_CODE, regionCode);
      }
    }

    /**
     * Next uses every part of the request (including headers) to form the cache key, so when caching we need to avoid setting high
     * cardinality headers on the request, e.g. User Agent, DeviceID etc
     */
    if (config.cache !== 'force-cache') {
      h.set(
        X_USER_AGENT,
        (await headersFunction.headers()).get('user-agent') || 'Unknown UA'
      );
    } else {
      h.set(
        X_USER_AGENT,
        `Next cached request - ${config.method} - ${config.url}`
      );
    }
  }

  if (config.withAuth) {
    let authCookie = getCookie(ACCESS_TOKEN_KEY);
    if (isServerCheck()) {
      authCookie = (await headersFunction.cookies()).get(
        ACCESS_TOKEN_KEY
      )?.value;
    }
    if (authCookie) {
      h.set(H_AUTHORIZATION, `Bearer ${authCookie}`);
    }
  }

  if (config.withDeviceId) {
    const persistentIdCookie = await getPersistentId();
    if (persistentIdCookie) {
      h.set(H_DEPOP_DEVICE_ID, persistentIdCookie);
    }
  }

  if (config.withUserId) {
    let userIdCookie = getCookie(USER_ID_KEY);
    if (isServerCheck()) {
      userIdCookie = (await headersFunction.cookies()).get(USER_ID_KEY)?.value;
    }
    if (userIdCookie) {
      h.set(X_USER_ID, userIdCookie);
    }
  }

  // Send preview env header
  let previewEnv = getCookie(PREVIEW_ENV_KEY);
  if (isServerCheck()) {
    previewEnv = (await headersFunction.cookies()).get(PREVIEW_ENV_KEY)?.value;
  }
  // Cloudfront requests fail if the ot-baggage-x-env-name header is set
  if (
    previewEnv &&
    !config.url?.startsWith(process.env.NEXT_PUBLIC_DEPOP_CLOUDFRONT_HOST)
  ) {
    h.set(X_ENV_NAME, previewEnv);
  }

  return h;
}

/**
 * Only stringify data if it is a proper object, i.e.
 * not a file or FormData
 *
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function processData(data: any) {
  if (data.constructor === Object || data.constructor === Array) {
    return JSON.stringify(data);
  }

  return data;
}

/**
 * Modified NextJS/Native Fetch to be compatible with Axios API
 */
async function patchedFetch<T = unknown>(
  config: RequestConfig
): Promise<AxiosCompatibleResponse<T>> {
  try {
    const headers = await modifyRequestHeaders(config);
    const queryParams = config.params
      ? queryParamsFromObject(config.params)
      : '';

    const builtConfig = {
      headers,
      method: config.method || 'GET',
      body: config.data ? processData(config.data) : undefined,
      cache: config.cache,
      next: {
        revalidate: config.revalidate,
      },
      keepalive: config.keepalive,
    };

    const fullUrl = config.url + queryParams;

    try {
      const urlOrigin = new URL(fullUrl).origin;

      // Forward incoming request headers (set by Cloudflare and web-routing) for
      // internal routes so backend services receive the same headers they'd get
      // via depop-routing. We exclude cookie, host, and hop-by-hop headers to
      // avoid breaking Next.js fetch caching (high-cardinality headers like cookie
      // create unique cache keys per user) and leaking sensitive data.
      // Headers already set by modifyRequestHeaders are not overridden.
      if (
        INTERNAL_ROUTE_HOSTS.includes(urlOrigin) &&
        config.cache !== 'force-cache'
      ) {
        const headersFunction = await nextHeaders;
        const incomingHeaders = await headersFunction.headers();
        incomingHeaders.forEach((value, name) => {
          if (
            !headers.has(name) &&
            !INTERNAL_ROUTE_EXCLUDED_HEADERS.has(name)
          ) {
            headers.set(name, value);
          }
        });
      }
    } catch {
      // fail silently and handle url as per normal

      /**
       * Use conditional dynamic import for the metrics module as this http
       * module is used in middleware, where the edge runtime does not support
       * the necessary native modules required for metrics.
       *
       * See:
       * - https://github.com/vercel/next.js/discussions/46722
       * - https://github.com/vercel/next.js/discussions/34179
       */
      if (process.env.NEXT_RUNTIME === 'nodejs') {
        const { recordMetric } =
          await import('@/modules/observability/metrics');

        recordMetric({
          type: 'increment',
          stat: Stdv1Stat['errors.http.urlError'],
        });
      }
    }

    let timeout = createTimeoutSignal(config.timeout);
    let response: Response;
    let responseData;
    try {
      response = await fetch(fullUrl, {
        ...builtConfig,
        signal: timeout.signal,
      });

      if (!response.ok) {
        try {
          const isTurnstileChallenged = isChallengeResponse(response);

          if (isTurnstileChallenged) {
            // Clear before the unbounded user challenge wait
            timeout.clear();

            document.dispatchEvent(new Event(TURNSTILE_EVENT_MODAL_TRIGGER));

            await new Promise<void>((resolve, reject) => {
              document.addEventListener(
                TURNSTILE_CHALLENGE_RESULT,
                (event) => {
                  const { isPassed } = (
                    event as CustomEvent<TurnstileChallengeResult>
                  ).detail;

                  if (isPassed) {
                    resolve();
                  } else {
                    reject();
                  }
                },
                { once: true }
              );
            });

            // Replay original request after challenge is passed with a fresh timeout
            timeout = createTimeoutSignal(config.timeout);
            response = await fetch(config.url + queryParams, {
              ...builtConfig,
              signal: timeout.signal,
            });
          }
        } catch {
          // fail silently and let the original response to be processed as failed
        }
      }

      if (isJSONResponse(response)) {
        responseData = await response.json();
      } else if (isTextResponse(response)) {
        responseData = await response.text();
      } else if (isOctetStreamResponse(response)) {
        responseData = await response.arrayBuffer();
      } else {
        responseData = null;
      }
    } finally {
      timeout.clear();
    }

    if (!response?.ok) {
      // Use originalUrl (public) instead of response.url to avoid leaking internal k8s hostnames in logs/errors
      throw new AxiosCompatibleError({
        code: response?.status,
        config: builtConfig,
        response: {
          url: fullUrl,
          data: responseData,
          statusText: response.statusText,
          status: response?.status,
          headers: response.headers,
        },
      });
    }

    return {
      data: responseData,
      status: response.status,
      headers: response.headers,
    };
  } catch (e) {
    /**
     * Use conditional dynamic import for the metrics module as this http
     * module is used in middleware, where the edge runtime does not support
     * the necessary native modules required for metrics.
     *
     * See:
     * - https://github.com/vercel/next.js/discussions/46722
     * - https://github.com/vercel/next.js/discussions/34179
     */
    if (process.env.NEXT_RUNTIME === 'nodejs') {
      const { recordMetric } = await import('@/modules/observability/metrics');

      recordMetric({
        type: 'increment',
        stat: Stdv1Stat['errors.http.networkError'],
      });
    }

    throw e;
  }
}

/**
 * Axios-like object for backwards compatibility with existing calls
 */
export const typedHttp = {
  get: function get<Path extends GenericPresentationPath>(
    url: `${string}${Path}`,
    config?: RequestConfig
  ) {
    return patchedFetch<SuccessResponse<Path, 'get'>>({
      url,
      method: 'GET',
      ...config,
    });
  },
  post: function post<Path extends GenericPresentationPath>(
    url: `${string}${Path}`,
    data: RequestBody<Path, 'post'>,
    config?: RequestConfig
  ) {
    return patchedFetch<SuccessResponse<Path, 'post'>>({
      url,
      method: 'POST',
      data,
      ...config,
    });
  },
  patch: function patch<Path extends GenericPresentationPath>(
    url: `${string}${Path}`,
    data: RequestBody<Path, 'patch'>,
    config?: RequestConfig
  ) {
    return patchedFetch<SuccessResponse<Path, 'patch'>>({
      url,
      method: 'PATCH',
      data,
      ...config,
    });
  },
  put: function put<Path extends GenericPresentationPath>(
    url: `${string}${Path}`,
    data: RequestBody<Path, 'put'>,
    config?: RequestConfig
  ) {
    return patchedFetch<SuccessResponse<Path, 'put'>>({
      url,
      method: 'PUT',
      data,
      ...config,
    });
  },
  delete: function del<Path extends GenericPresentationPath>(
    url: `${string}${Path}`,
    config?: RequestConfig
  ) {
    return patchedFetch<SuccessResponse<Path, 'delete'>>({
      url,
      method: 'DELETE',
      ...config,
    });
  },
};
//  * Axios-like object for backwards compatibility with existing calls
//  */
export const http = {
  get: function get<T>(url: string, config?: RequestConfig) {
    return patchedFetch<T>({ url, method: 'GET', ...config });
  },
  post: function post<T>(url: string, data?: unknown, config?: RequestConfig) {
    return patchedFetch<T>({ url, method: 'POST', data, ...config });
  },
  patch: function patch<T>(
    url: string,
    data?: unknown,
    config?: RequestConfig
  ) {
    return patchedFetch<T>({ url, method: 'PATCH', data, ...config });
  },
  put: function put<T>(url: string, data?: unknown, config?: RequestConfig) {
    return patchedFetch<T>({ url, method: 'PUT', data, ...config });
  },
  delete: function del<T>(url: string, config?: RequestConfig) {
    return patchedFetch<T>({ url, method: 'DELETE', ...config });
  },
};

export default http;
